import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, PenLine, Library, ShieldCheck, Camera, UserCog,
  CreditCard, LogOut, Menu, X, Layers, Wrench, Globe, ImageIcon, Film, BarChart3,
} from "lucide-react";
import GenerateView from "@/components/dashboard/GenerateView";
import ListingsView from "@/components/dashboard/ListingsView";
import ComplianceView from "@/components/dashboard/ComplianceView";
import PhotoView from "@/components/dashboard/PhotoView";
import VoiceView from "@/components/dashboard/VoiceView";
import BillingView from "@/components/dashboard/BillingView";
import BulkView from "@/components/dashboard/BulkView";
import ToolkitView from "@/components/dashboard/ToolkitView";
import LandingPagesView from "@/components/dashboard/LandingPagesView";
import GraphicsView from "@/components/dashboard/GraphicsView";
import VideoView from "@/components/dashboard/VideoView";
import IntelligenceView from "@/components/dashboard/IntelligenceView";

const NAV = [
  { id: "generate", label: "Generate", icon: PenLine },
  { id: "toolkit", label: "Toolkit", icon: Wrench },
  { id: "landing", label: "Landing Pages", icon: Globe },
  { id: "graphics", label: "Social Graphics", icon: ImageIcon },
  { id: "video", label: "Listing Video", icon: Film },
  { id: "intelligence", label: "Intelligence", icon: BarChart3 },
  { id: "bulk", label: "Bulk Generate", icon: Layers },
  { id: "library", label: "My Listings", icon: Library },
  { id: "compliance", label: "Compliance", icon: ShieldCheck },
  { id: "photo", label: "Photo-to-Copy", icon: Camera },
  { id: "voice", label: "Voice Profile", icon: UserCog },
  { id: "billing", label: "Billing", icon: CreditCard },
];

const PLAN_LABEL = { free: "Free", starter: "Starter", pro: "Pro", brokerage: "Brokerage" };

export default function Dashboard() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "generate");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [videoPrefill, setVideoPrefill] = useState(null);

  const makeVideoFromListing = (l) => {
    const d = l.details || {};
    const specs = [d.beds && `${d.beds} bd`, d.baths && `${d.baths} ba`, d.sqft && `${d.sqft} sqft`].filter(Boolean).join(" · ");
    const headline = (l.outputs?.headlines || "")
      .split("\n").map((s) => s.replace(/^[-•\d.\s"]+/, "").trim()).filter(Boolean)[0] || "";
    const captions = [l.title, specs, d.location, headline].filter(Boolean);
    setVideoPrefill({ badge: "Just Listed", captions, agent_name: user?.name || "", ts: Date.now() });
    setTab("video");
  };

  useEffect(() => {
    if (params.get("tab")) setTab(params.get("tab"));
  }, [params]);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const limit = user?.listings_limit;
  const used = user?.listings_used || 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-sans text-slate-900 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-200">
          <Building2 className="h-5 w-5 text-slate-900" />
          <span className="font-display text-xl tracking-tight">Penhaus<span className="text-[#D4AF37]">AI</span></span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => { setTab(n.id); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${tab === n.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="px-3 py-2 rounded-md bg-slate-50 mb-2" data-testid="plan-badge">
            <p className="text-xs text-slate-500">Current plan</p>
            <p className="font-semibold text-sm">{PLAN_LABEL[user?.plan] || "Free"}</p>
            <p className="text-xs text-slate-500 mt-1" data-testid="usage-indicator">
              {limit == null ? `${used} listings · Unlimited` : `${used} / ${limit} listings this month`}
            </p>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <h1 className="font-semibold text-lg capitalize hidden sm:block">{NAV.find((n) => n.id === tab)?.label}</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:block">{user?.name}</span>
            <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <main className="p-6 lg:p-8 max-w-6xl mx-auto">
          {tab === "generate" && <GenerateView onUsed={checkAuth} />}
          {tab === "toolkit" && <ToolkitView />}
          {tab === "landing" && <LandingPagesView />}
          {tab === "graphics" && <GraphicsView />}
          {tab === "video" && <VideoView prefill={videoPrefill} />}
          {tab === "intelligence" && <IntelligenceView />}
          {tab === "bulk" && <BulkView onGoBilling={() => setTab("billing")} onUsed={checkAuth} />}
          {tab === "library" && <ListingsView onMakeVideo={makeVideoFromListing} />}
          {tab === "compliance" && <ComplianceView />}
          {tab === "photo" && <PhotoView onGoGenerate={() => setTab("generate")} />}
          {tab === "voice" && <VoiceView onSaved={checkAuth} />}
          {tab === "billing" && <BillingView onChange={checkAuth} />}
        </main>
      </div>
    </div>
  );
}
